import {
  isRecord,
  jsonArray,
  jsonBoolean,
  jsonHex,
  jsonInt,
  jsonNumber,
  jsonNumbers,
  jsonText,
  parseJson,
} from '../../json';
import {
  ACK_STATUS,
  COREDUMP_RESET,
  PANEL_STATE,
  PANEL_STATUS,
  RCU_STATUS_TYPE,
} from '../catalog/ack';
import type { AckStatus, CoredumpReset } from '../catalog/ack';
import { daliModeFromName } from '../catalog/dali';
import { logDropNotice } from '../catalog/diagnostics';
import { MAPPING_ACTION_KEYS } from '../catalog/mapping';
import { DIN_MODULE, RCU_ACK_TYPE, RCU_INBOUND_TYPE, moduleIdFromWireType } from '../catalog/protocol';
import type { DinModuleId } from '../catalog/protocol';
import type {
  CoredumpAckReading,
  CoredumpChunkReading,
  CoredumpInfoReading,
  DinAckReading,
  DinStatusAllReading,
  DinStatusReading,
  DaliAckReading,
  DaliOperateReading,
  LedMappingReadReading,
  LoggingAckReading,
  LoggingStatusReading,
  LogsAckReading,
  MappingActionReading,
  MappingEntryReading,
  MappingReadReading,
  MemoryPool,
  MemoryStatus,
  NotifyReading,
  OtaAckReading,
  OtaProgressReading,
  PanelReading,
  RcuReading,
  RelayAckReading,
  RelayStatusReading,
  S3AckReading,
  S3ProgressReading,
  StatusReading,
  WifiStatus,
} from './readings';

/**
 * Turns one MQTT message into a reading.
 *
 * The firmware's replies are uneven by design: some are wrapped in `rcuack`,
 * some are raw documents, `listenpanel` means three different things, and a
 * module id arrives as `"52"` in one place and `2` in another. Everything is
 * normalised here so the store and the panels only ever see one shape per fact.
 *
 * Nothing throws and nothing is dropped silently: a message that cannot be read
 * comes back as an `unknown` reading carrying the raw text and the reason.
 */

const unknown = (raw: string, reason: string): RcuReading => ({ kind: 'unknown', raw, reason });

const ackStatus = (value: unknown): AckStatus =>
  jsonText(value) === ACK_STATUS.FAIL ? ACK_STATUS.FAIL : ACK_STATUS.OK;

const resetReason = (value: unknown): CoredumpReset => {
  const text = jsonText(value);
  return Object.values(COREDUMP_RESET).find((candidate) => candidate === text) ?? COREDUMP_RESET.UNKNOWN;
};

/** `"52"`, `"5a"` or `"2"` all name the same module. */
const moduleId = (value: unknown): DinModuleId | null => {
  const raw = jsonHex(value);
  if (raw === null) return null;
  const id = raw > DIN_MODULE.DALI_BRIDGE ? moduleIdFromWireType(raw) : raw;
  return Object.values(DIN_MODULE).find((candidate) => candidate === id) ?? null;
};

const int = (value: unknown): number => {
  const number = jsonNumber(value);
  // The firmware casts incoming numbers to uint8_t, so truncate the same way.
  return number === null ? 0 : Math.trunc(number);
};

const memoryPool = (value: unknown): MemoryPool => {
  const record = isRecord(value) ? value : {};
  return { free: int(record.free), total: int(record.total), minFree: int(record.min_free) };
};

const memoryStatus = (value: unknown): MemoryStatus | null => {
  if (!isRecord(value)) return null;
  return { internal: memoryPool(value.internal), psram: memoryPool(value.psram) };
};

const wifiStatus = (value: unknown): WifiStatus | null => {
  if (!isRecord(value)) return null;
  return { ssid: jsonText(value.ssid), rssi: int(value.rssi), channel: int(value.channel) };
};

const mappingEntry = (value: unknown): MappingEntryReading | null => {
  if (!isRecord(value)) return null;
  // Action fields, kept as their raw strings for display and re-editing.
  const actions: readonly MappingActionReading[] = MAPPING_ACTION_KEYS.flatMap((field) =>
    typeof value[field] === 'string' ? [{ field, raw: jsonText(value[field]) }] : [],
  );
  return {
    atype: jsonText(value.atype),
    x: jsonNumbers(value.x),
    key: jsonInt(value.k),
    actions,
  };
};

const mappingEntries = (value: unknown): readonly MappingEntryReading[] =>
  jsonArray(value).flatMap((entry) => {
    const decoded = mappingEntry(entry);
    return decoded === null ? [] : [decoded];
  });

const panelReading = (data: Record<string, unknown>): PanelReading => {
  const status = jsonText(data.status);
  if (status === PANEL_STATUS.TIMEOUT) {
    return { kind: 'panel', event: { state: PANEL_STATE.TIMEOUT } };
  }
  if (status === PANEL_STATUS.OK) {
    return { kind: 'panel', event: { state: PANEL_STATE.STARTED } };
  }
  return {
    kind: 'panel',
    event: {
      state: PANEL_STATE.OPERATION,
      group: int(data.groupid),
      addr: int(data.addr),
      channel: int(data.channel),
      value: int(data.value),
    },
  };
};

/** Responses that carry nothing but a status, listed so the switch stays exhaustive. */
const STATUS_ONLY_TYPES = [
  RCU_STATUS_TYPE.MAPPING_ACK,
  RCU_STATUS_TYPE.MAPPING_ERASE,
  RCU_STATUS_TYPE.MAPPING_ERASE_ALL,
  RCU_STATUS_TYPE.LED_MAPPING_ACK,
  RCU_STATUS_TYPE.LED_MAPPING_ERASE,
] as const;

/**
 * Reads one `rcuack` body. `statustype` is the discriminator; anything else is
 * an unknown reply and is reported as such.
 */
const decodeAckData = (data: Record<string, unknown>): RcuReading => {
  const statusType = jsonText(data.statustype);

  switch (statusType) {
    case RCU_STATUS_TYPE.RELAY_ACK: {
      const reading: RelayAckReading = {
        kind: 'relayAck',
        group: int(data.groupid),
        relay: int(data.relayno),
        status: ackStatus(data.status),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.RELAY_STATUS: {
      const reading: RelayStatusReading = {
        kind: 'relayStatus',
        // Unsolicited reports deliberately omit the group.
        group: jsonInt(data.groupid),
        relay: int(data.relayno),
        value: int(data.value),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.ALL_RELAYS_STATUS:
      return { kind: 'allRelays', values: jsonNumbers(data.relayarray) };

    case RCU_STATUS_TYPE.DIN_ACK: {
      const reading: DinAckReading = {
        kind: 'dinAck',
        group: int(data.groupid),
        module: moduleId(data.dintype),
        addr: int(data.addr),
        channel: int(data.channel),
        status: ackStatus(data.status),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.DIN_STATUS: {
      const reading: DinStatusReading = {
        kind: 'dinStatus',
        group: int(data.groupid),
        module: moduleId(data.dintype),
        addr: int(data.addr),
        channel: int(data.channel),
        value: int(data.value),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.DIN_STATUS_ALL: {
      const reading: DinStatusAllReading = {
        kind: 'dinStatusAll',
        group: int(data.groupid),
        module: moduleId(data.dintype),
        addr: int(data.addr),
        values: jsonNumbers(data.valarray),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.DALI_OPERATE: {
      const reading: DaliOperateReading = {
        kind: 'daliOperateAck',
        master: int(data.master),
        address: int(data.daliaddr),
        mode: int(data.mode),
        status: ackStatus(data.status),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.DALI_ACK: {
      const reading: DaliAckReading = {
        kind: 'daliAck',
        master: int(data.master),
        address: int(data.daliaddr),
        success: jsonBoolean(data.success),
        mode: daliModeFromName(jsonText(data.mode)),
        value: int(data.value),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.LISTEN_PANEL:
      return panelReading(data);

    case RCU_STATUS_TYPE.NOTIFY_EVENT: {
      const reading: NotifyReading = {
        kind: 'notify',
        group: int(data.groupid),
        addr: int(data.addr),
        channel: int(data.channel),
        value: int(data.value),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.OTA_PROGRESS: {
      const reading: OtaProgressReading = { kind: 'otaProgress', progress: int(data.progress) };
      return reading;
    }

    case RCU_STATUS_TYPE.OTA_ACK: {
      const reading: OtaAckReading = { kind: 'otaAck', status: ackStatus(data.status) };
      return reading;
    }

    case RCU_STATUS_TYPE.LOGGING_ACK: {
      const reading: LoggingAckReading = {
        kind: 'loggingAck',
        status: ackStatus(data.status),
        mqtt: jsonBoolean(data.mqtt),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.LOGGING_STATUS: {
      const reading: LoggingStatusReading = {
        kind: 'loggingStatus',
        /** Unreliable in firmware: ERROR reads as enabled too. */
        enabled: jsonBoolean(data.enabled),
        mqtt: jsonBoolean(data.mqtt),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.LOGS_ACK: {
      const reading: LogsAckReading = {
        kind: 'logsAck',
        status: ackStatus(data.status),
        pending: jsonInt(data.pending),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.COREDUMP_INFO: {
      const reading: CoredumpInfoReading = {
        kind: 'coredumpInfo',
        present: jsonBoolean(data.present),
        crashSeq: int(data.crashseq),
        reset: resetReason(data.reset),
        dumpId: jsonText(data.dumpid),
        size: int(data.size),
        uploads: int(data.uploads),
        task: jsonText(data.task),
        pc: jsonText(data.pc),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.COREDUMP_ACK: {
      const reading: CoredumpAckReading = {
        kind: 'coredumpAck',
        status: ackStatus(data.status),
        dumpId: jsonText(data.dumpid),
        crashSeq: jsonInt(data.crashseq),
        uploads: jsonInt(data.uploads),
        chunks: jsonInt(data.chunks),
        erased: jsonBoolean(data.erased),
        error: jsonText(data.error),
      };
      return reading;
    }

    case RCU_STATUS_TYPE.S3_PROGRESS: {
      const reading: S3ProgressReading = { kind: 's3Progress', progress: int(data.progress) };
      return reading;
    }

    case RCU_STATUS_TYPE.S3_MAPPING_ACK: {
      const reading: S3AckReading = {
        kind: 's3Ack',
        status: ackStatus(data.status),
        scenes: jsonInt(data.key_mappings),
        led: jsonInt(data.led_mappings),
        dali: jsonInt(data.dali_mappings),
        error: jsonText(data.error),
      };
      return reading;
    }

    default:
      if (STATUS_ONLY_TYPES.some((candidate) => candidate === statusType)) {
        return { kind: 'commandResult', statusType, status: ackStatus(data.status) };
      }
      return unknown(JSON.stringify(data), `Unknown statustype "${statusType}"`);
  }
};

/** The status document, as sent retained, on request, or by `GET /status`. */
const decodeStatusDocument = (data: Record<string, unknown>): StatusReading => ({
  kind: 'status',
  status: jsonText(data.status),
  serial: jsonText(data.serialno),
  mac: jsonText(data.mac),
  ip: jsonText(data.ip),
  uptime: jsonText(data.uptime),
  wifi: wifiStatus(data.wifi),
  memory: memoryStatus(data.memory),
  firmware: isRecord(data.firmware)
    ? { version: jsonText(data.firmware.version), buildDate: jsonText(data.firmware.build_date) }
    : null,
});

/** One coredump chunk: base64 of at most 3072 raw bytes of the image. */
const decodeCoredumpChunk = (data: Record<string, unknown>): CoredumpChunkReading => ({
  kind: 'coredumpChunk',
  dumpId: jsonText(data.dumpid),
  crashSeq: int(data.crashseq),
  upload: int(data.upload),
  seq: int(data.seq),
  total: int(data.total),
  /** The whole image's size, not this chunk's. */
  size: int(data.size),
  base64: jsonText(data.b64),
});

/** A keypad scene's received frame: eight frame bytes and a trailing status. */
const decodeRxFrame = (raw: string, data: unknown): RcuReading => {
  const bytes = jsonNumbers(data);
  if (bytes.length === 0) return unknown(raw, 'rxdata carried no frame bytes');
  const statusByte = bytes[bytes.length - 1] ?? 0;
  return { kind: 'rxFrame', bytes: bytes.slice(0, -1), statusByte };
};

/** Reads whatever arrived on the event topic, wrapped or raw. */
export const decodeInbound = (raw: string): RcuReading => {
  const parsed = parseJson(raw);
  if (!isRecord(parsed)) return unknown(raw, 'Not a JSON object');

  const type = jsonText(parsed.type);
  if (type === RCU_ACK_TYPE) {
    return isRecord(parsed.data)
      ? decodeAckData(parsed.data)
      : unknown(raw, 'rcuack without a data object');
  }
  if (type === RCU_INBOUND_TYPE.STATUS) {
    return isRecord(parsed.data) ? decodeStatusDocument(parsed.data) : unknown(raw, 'status without data');
  }
  if (type === RCU_INBOUND_TYPE.MAPPING_STATUS) {
    const reading: MappingReadReading = { kind: 'mappingRead', entries: mappingEntries(parsed.data) };
    return reading;
  }
  if (type === RCU_INBOUND_TYPE.LED_MAPPING_STATUS) {
    const reading: LedMappingReadReading = {
      kind: 'ledMappingRead',
      entries: mappingEntries(parsed.data),
    };
    return reading;
  }
  if (type === RCU_INBOUND_TYPE.COREDUMP_CHUNK) {
    return isRecord(parsed.data)
      ? decodeCoredumpChunk(parsed.data)
      : unknown(raw, 'coredump chunk without data');
  }
  if (type === RCU_INBOUND_TYPE.RX_DATA) {
    return decodeRxFrame(raw, parsed.data);
  }
  return unknown(raw, `Unknown document type "${type}"`);
};

/** One log batch: newline-separated raw text, and the firmware's own drop notice. */
export interface LogBatch {
  readonly lines: readonly string[];
  /** Lines the firmware says it dropped while assembling this batch. */
  readonly dropped: number;
}

export const decodeLogBatch = (text: string): LogBatch => {
  const lines = text.split('\n').filter((line) => line.trim() !== '');
  const dropped = lines.reduce((total, line) => total + (logDropNotice(line) ?? 0), 0);
  return { lines, dropped };
};
